/**
 * 秒数 → 人类可读时间码。统一全仓 8+ 处重复的 m:ss / mm:ss.xxx / h:mm:ss 格式化。
 *
 * 设计：
 * - 一个函数覆盖所有变体，靠 options 切换精度（ms）和是否进位到小时（hours）
 * - 占位符可定制（不同调用点对"无效值"的语义不同：播放器 0:00 / 复习卡 --:--）
 * - 无效输入（null/undefined/NaN/负数）统一走 placeholder，不再各处自己判 isFinite
 */

export interface FormatTimeOptions {
  /** 输出毫秒精度，如 mm:ss.xxx。默认 false（m:ss） */
  ms?: boolean;
  /** 超过 1 小时进位到 h:mm:ss（不带小时时分钟累加可超 59，字幕场景够用）。默认 false */
  hours?: boolean;
  /** 无效输入（null/undefined/NaN/负数）的返回值。默认 '0:00' / '0:00:00' / '0:00.000'（随其它选项） */
  placeholder?: string;
}

/**
 * 秒数 → 时间码字符串。
 *
 * @example
 * formatTimecode(75)              // '1:15'
 * formatTimecode(75, { hours: true }) // '0:01:15'
 * formatTimecode(75.234, { ms: true }) // '01:15.234'
 * formatTimecode(null)            // '0:00'
 * formatTimecode(null, { placeholder: '--:--' }) // '--:--'
 */
export function formatTimecode(
  seconds: number | null | undefined,
  options: FormatTimeOptions = {},
): string {
  const { ms = false, hours = false } = options;
  const defaultPlaceholder = hours ? '0:00:00' : ms ? '0:00.000' : '0:00';
  const placeholder = options.placeholder ?? defaultPlaceholder;

  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return placeholder;
  }

  const totalMs = ms ? Math.round(seconds * 1000) : Math.floor(seconds) * 1000;
  const milli = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const sec = totalSec % 60;
  const totalMin = Math.floor(totalSec / 60);
  const min = hours ? totalMin % 60 : totalMin;
  const h = Math.floor(totalMin / 60);
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');

  // 分钟位补零规则随格式而定（匹配各来源原行为）：
  //   hours（h:mm:ss 标准）→ 补零
  //   ms（admin 字幕时间码 mm:ss.xxx）→ 补零
  //   纯 m:ss（web 播放器）→ 不补零（1:15 而非 01:15）
  const padMin = hours || ms;
  const minPart = padMin ? pad(min) : String(min);

  const minSecCore = hours ? `${h}:${minPart}:${pad(sec)}` : `${minPart}:${pad(sec)}`;

  return ms ? `${minSecCore}.${pad(milli, 3)}` : minSecCore;
}
